/**
 * useAmbientTune — Analyzes a listing image to determine ambient glow intensity.
 *
 * Draws the image onto a tiny offscreen canvas, samples pixel data to compute
 * average luminance and saturation. Uses a three-tier classification system:
 *
 * - VIBRANT: dark, colourful images (luminance <= 0.35 AND saturation >= 0.25)
 * - MODERATE: middle-ground images that don't fit either extreme
 * - MUTED: light or desaturated images (luminance > 0.6 OR saturation < 0.15)
 *
 * Sets on document.documentElement:
 *   --ambient-opacity       (0.5 vibrant, 0.25 moderate, 0.15 muted)
 *   --ambient-saturate      (1.5 vibrant, 1.2 moderate, 1.0 muted)
 *   --ambient-brightness    (1 vibrant, 0.85 moderate, 0.6 muted)
 *   data-ambient-tone       ("vibrant" | "moderate" | "muted")
 */

import { useEffect, useRef } from 'react';

// Thresholds for three-tier classification
const MUTED_LUMINANCE = 0.6; // above this = light → muted
const MUTED_SATURATION = 0.15; // below this = gray → muted
const VIBRANT_LUMINANCE = 0.35; // below this (+ saturated) = vibrant
const VIBRANT_SATURATION = 0.25; // above this (+ dark) = vibrant

// CSS custom property presets
const VIBRANT = {
  '--ambient-opacity': '0.5',
  '--ambient-opacity-page': '0.30',
  '--ambient-opacity-carousel': '0.8',
  '--ambient-saturate': '1.5',
  '--ambient-saturate-page': '1.4',
  '--ambient-saturate-carousel': '1.8',
  '--ambient-brightness': '1',
  '--ambient-brightness-carousel': '1.08',
};

const MODERATE = {
  '--ambient-opacity': '0.25',
  '--ambient-opacity-page': '0.20',
  '--ambient-opacity-carousel': '0.52',
  '--ambient-saturate': '1.2',
  '--ambient-saturate-page': '1.2',
  '--ambient-saturate-carousel': '1.4',
  '--ambient-brightness': '0.85',
  '--ambient-brightness-carousel': '0.96',
};

const MUTED = {
  '--ambient-opacity': '0.15',
  '--ambient-opacity-page': '0.12',
  '--ambient-opacity-carousel': '0.32',
  '--ambient-saturate': '1.0',
  '--ambient-saturate-page': '1.0',
  '--ambient-saturate-carousel': '1.0',
  '--ambient-brightness': '0.6',
  '--ambient-brightness-carousel': '0.75',
};

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  }

  return { s, l };
}

function analyzeImageData(imageData) {
  const { data } = imageData;
  const pixelCount = data.length / 4;
  let totalL = 0;
  let totalS = 0;

  for (let i = 0; i < data.length; i += 4) {
    const { s, l } = rgbToHsl(data[i], data[i + 1], data[i + 2]);
    totalL += l;
    totalS += s;
  }

  return {
    luminance: totalL / pixelCount,
    saturation: totalS / pixelCount,
  };
}

const PROP_KEYS = Object.keys(VIBRANT);

export default function useAmbientTune(imageUrl) {
  const prevUrlRef = useRef(null);

  useEffect(() => {
    if (!imageUrl || typeof window === 'undefined') return;
    if (imageUrl === prevUrlRef.current) return;
    prevUrlRef.current = imageUrl;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      // Draw to a tiny canvas for fast sampling
      const canvas = document.createElement('canvas');
      const size = 32;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, size, size);

      let stats;
      try {
        const imageData = ctx.getImageData(0, 0, size, size);
        stats = analyzeImageData(imageData);
      } catch {
        // CORS or tainted canvas — default to vibrant
        return;
      }

      // Three-tier classification: vibrant → moderate → muted
      const isLight = stats.luminance > MUTED_LUMINANCE;
      const isGray = stats.saturation < MUTED_SATURATION;
      const isDark = stats.luminance <= VIBRANT_LUMINANCE;
      const isSaturated = stats.saturation >= VIBRANT_SATURATION;

      let preset, tone;
      if (isLight || isGray) {
        preset = MUTED;
        tone = 'muted';
      } else if (isDark && isSaturated) {
        preset = VIBRANT;
        tone = 'vibrant';
      } else {
        preset = MODERATE;
        tone = 'moderate';
      }

      const root = document.documentElement;

      PROP_KEYS.forEach(key => {
        root.style.setProperty(key, preset[key]);
      });
      root.setAttribute('data-ambient-tone', tone);
    };

    img.src = imageUrl;

    return () => {
      img.onload = null;
    };
  }, [imageUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const root = document.documentElement;
      PROP_KEYS.forEach(key => root.style.removeProperty(key));
      root.removeAttribute('data-ambient-tone');
      prevUrlRef.current = null;
    };
  }, []);
}
